import DayjsUtils from '@date-io/dayjs';
import {
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import AutocompleteTree from 'components/atoms/AutocompleteTree';
import { IAutocompleteTreeRef } from 'components/atoms/AutocompleteTree/types';
import LoadingInputEndAdornment from 'components/atoms/LoadingInputEndAdornment';
import dayjs from 'dayjs';
import ptBR from 'dayjs/locale/pt-br';
import duration from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useAuth } from 'hooks/useAuth';
import { IEquipmentFilterTree } from 'interfaces/Equipment';
import { debounce } from 'lodash';
import React from 'react';
import { useQuery } from 'react-query';
import { postEquipmentOptionsByInstallation } from 'services/equipment';
import useStyles from './style';
import { IFormAddInspection, IOption } from './types';

const DEFAULT_HELPER_TEXT = 'Obrigatório';
const OPTIONAL = 'Opcional';
const NO_OPTIONS_TEXT = 'Nenhum resultado encontrado';

const FORM_ID = 'ADD_INSPECTION';

const DEFAULT_TYPE_MAINTENANCE = 'Preventiva';

dayjs.extend(isoWeek);
dayjs.extend(duration);

const FormAddInspection = ({
  formik,
  options,
  setFieldsSearchText,
}: IFormAddInspection) => {
  const classes = useStyles();
  const { account } = useAuth();

  const shortName = React.useMemo(() => {
    if (!account?.username) return null;
    return account?.username.split('@')[0];
  }, [account]);

  const inspectorTechnicianOptions = React.useMemo<IOption[]>(() => {
    if (!shortName) return [];

    return [{ label: shortName, value: shortName }];
  }, [shortName]);

  const enableQueryEquipmentOptions = React.useMemo(() => {
    return Boolean(formik.values.installationId);
  }, [formik.values.installationId]);

  const equipmentTreeRef = React.useRef<IAutocompleteTreeRef>(null);

  const [equipmentOptions, setEquipmentOptions] = React.useState<
    IEquipmentFilterTree[]
  >([]);

  const defaultValueForMaintenanceType = React.useMemo(() => {
    if (options?.typeMaintenanceOptions?.values?.length > 0) {
      const maintenance = options?.typeMaintenanceOptions?.values?.find(
        item =>
          item.label.toLowerCase() === DEFAULT_TYPE_MAINTENANCE.toLowerCase(),
      );
      if (maintenance) {
        const { label, value } = maintenance;
        return {
          label,
          value,
        };
      }
    }

    return { label: '', value: '' };
  }, [options?.typeMaintenanceOptions?.values]);

  const defaultExpandedEquipments = React.useMemo(() => {
    if (equipmentOptions.length) {
      return [equipmentOptions[0].id];
    }

    return undefined;
  }, [equipmentOptions]);

  // Só posso fazer queries após abrir o modal
  const defaultValueForExecutor = React.useMemo(() => {
    if (options?.executorOptions?.values?.length > 0) {
      const { label, value } = options?.executorOptions?.values[0];

      return {
        label,
        value,
      };
    }

    return { label: '', value: '' };
  }, [options?.executorOptions?.values]);

  const handleAutoCompleteChange = React.useCallback(
    (name: string, option: IOption) => {
      const value = option?.value || '';
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  const debounceSetFieldSearchText = React.useMemo(
    () =>
      debounce((name, value) => {
        setFieldsSearchText(currentValues => ({
          ...currentValues,
          [name]: value,
        }));
      }, 600),
    [setFieldsSearchText],
  );

  const handleFieldSearchTextChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      if (value.length >= 2) {
        debounceSetFieldSearchText(name, value);
      } else {
        debounceSetFieldSearchText(name, '');
      }
    },
    [debounceSetFieldSearchText],
  );

  const queryEquipmentOptions = useQuery({
    queryKey: ['EquipmentOptionsContext', [formik?.values.installationId]],
    queryFn: async () => {
      const params = {
        installationIds: [formik?.values.installationId],
      };

      const { data } = await postEquipmentOptionsByInstallation(params);
      return data;
    },
    onSuccess: data => {
      setEquipmentOptions(data.result);
    },
    enabled: enableQueryEquipmentOptions,
  });

  const getOptionSelected = (option: IOption, anotherOption: IOption) =>
    option.value === anotherOption.value;

  const getInputValue = (
    inputOptions: IOption[] | undefined,
    selectedValue: string | number | undefined,
  ) => inputOptions?.find(item => item.value === selectedValue)?.label || '';

  const inputRenderer = React.useCallback(
    (params: TextFieldProps, loading?: boolean) => (
      <TextField
        {...params}
        className={classes.formBaseInput}
        variant="outlined"
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              <LoadingInputEndAdornment loading={loading} />
              {params?.InputProps?.endAdornment}
            </>
          ),
        }}
      />
    ),
    [classes.formBaseInput],
  );

  return (
    <form
      id={FORM_ID}
      className={classes.bodyContent}
      onSubmit={formik.handleSubmit}
    >
      <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
        <Grid className={classes.gridContent} container spacing={2}>
          {/* START ROW 1 */}
          <Grid container direction="row" alignItems="center" spacing={2}>
            <Grid item xs={4}>
              <Autocomplete
                id="clusterId"
                className={classes.inputList}
                options={options?.clusterOptions?.values}
                onChange={(e, selected) => {
                  // sempre que alterar o cluster selecionado
                  // tem que limpar a instalação selecionada
                  formik.setValues(oldValues => ({
                    ...oldValues,
                    installationId: '',
                    activityId: '',
                    equipmentId: '',
                  }));

                  if (equipmentTreeRef.current)
                    equipmentTreeRef.current.onReset();

                  return handleAutoCompleteChange(
                    'clusterId',
                    selected as IOption,
                  );
                }}
                getOptionLabel={option => String(option.label)}
                getOptionSelected={getOptionSelected}
                noOptionsText={NO_OPTIONS_TEXT}
                renderInput={params => {
                  const extraProps = {
                    name: 'clusterId',
                    label: 'Cluster',
                    error:
                      formik.touched.clusterId &&
                      Boolean(formik.errors.clusterId),
                    helperText:
                      (formik.touched.clusterId && formik.errors.clusterId) ||
                      DEFAULT_HELPER_TEXT,
                  };
                  return inputRenderer(
                    { ...params, ...extraProps },
                    options?.clusterOptions?.isLoading,
                  );
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                id="installationId"
                className={classes.inputList}
                options={options?.installationOptions?.values}
                disabled={!formik.values?.clusterId}
                disableClearable={!formik.values.installationId}
                inputValue={getInputValue(
                  options?.installationOptions?.values,
                  formik.values?.installationId,
                )}
                onChange={(e, selected) => {
                  formik.setValues(oldValues => ({
                    ...oldValues,
                    activityId: '',
                    equipmentId: '',
                  }));
                  if (equipmentTreeRef.current)
                    equipmentTreeRef.current.onReset();
                  setEquipmentOptions([]);
                  return handleAutoCompleteChange(
                    'installationId',
                    selected as IOption,
                  );
                }}
                getOptionLabel={option => String(option.label)}
                getOptionSelected={getOptionSelected}
                noOptionsText={NO_OPTIONS_TEXT}
                renderInput={params => {
                  const extraProps = {
                    name: 'installationId',
                    label: 'Instalação',
                    error:
                      formik.touched.installationId &&
                      Boolean(formik.errors.installationId),
                    helperText:
                      (formik.touched.installationId &&
                        formik.errors.installationId) ||
                      DEFAULT_HELPER_TEXT,
                  };
                  return inputRenderer(
                    { ...params, ...extraProps },
                    options?.installationOptions?.isLoading,
                  );
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                id="activityId"
                className={classes.inputList}
                options={options?.activityOptions?.values}
                disabled={!formik.values.installationId}
                inputValue={
                  getInputValue(
                    options?.activityOptions?.values,
                    formik.values?.activityId,
                  ) || formik.values?.activityId
                }
                onChange={(e, selected) => {
                  return handleAutoCompleteChange(
                    'activityId',
                    selected as IOption,
                  );
                }}
                onInputChange={(e, newValue, reason) => {
                  if (reason === 'reset') {
                    formik.setFieldValue('activityId', '');
                  } else {
                    formik.setFieldValue('activityId', newValue);
                  }
                }}
                getOptionLabel={option => String(option.label)}
                getOptionSelected={getOptionSelected}
                noOptionsText={NO_OPTIONS_TEXT}
                renderInput={params => {
                  const extraProps = {
                    name: 'activityId',
                    label: 'Título da atividade',
                    onChange: handleFieldSearchTextChange,
                    error:
                      formik.touched.activityId &&
                      Boolean(formik.errors.activityId),
                    helperText:
                      (formik.touched.activityId && formik.errors.activityId) ||
                      DEFAULT_HELPER_TEXT,
                  };
                  return inputRenderer(
                    {
                      ...params,
                      ...extraProps,
                    },
                    options?.activityOptions?.isLoading,
                  );
                }}
              />
            </Grid>
          </Grid>
          {/* END ROW 1 */}

          {/* START ROW 2 */}
          <Grid container direction="row" alignItems="center" spacing={2}>
            <Grid item xs={4}>
              <Autocomplete
                id="maintenance"
                className={classes.inputList}
                options={options?.typeMaintenanceOptions?.values}
                onChange={(e, selected) => {
                  return handleAutoCompleteChange(
                    'maintenance',
                    selected as IOption,
                  );
                }}
                disabled
                value={defaultValueForMaintenanceType}
                getOptionLabel={option => String(option.label)}
                getOptionSelected={getOptionSelected}
                noOptionsText={NO_OPTIONS_TEXT}
                renderInput={params => {
                  const extraProps = {
                    name: 'maintenance',
                    label: 'Tipo de manutenção',
                    error:
                      formik.touched.maintenance &&
                      Boolean(formik.errors.maintenance),
                    helperText:
                      (formik.touched.maintenance &&
                        formik.errors.maintenance) ||
                      DEFAULT_HELPER_TEXT,
                  };
                  return inputRenderer(
                    { ...params, ...extraProps },
                    options?.typeMaintenanceOptions?.isLoading,
                  );
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <AutocompleteTree
                ref={equipmentTreeRef}
                id="equipmentId"
                name="equipmentId"
                defaultExpanded={defaultExpandedEquipments}
                noOptionsText="Sem opções disponíveis"
                className={classes.inputList}
                disabled={!formik.values.installationId}
                items={equipmentOptions}
                label="Equipamento"
                placeholder=""
                extraTextFieldProps={{
                  error:
                    formik.touched.equipmentId &&
                    Boolean(formik.errors.equipmentId),
                  helperText:
                    (formik.touched.equipmentId && formik.errors.equipmentId) ||
                    DEFAULT_HELPER_TEXT,
                }}
                loading={
                  queryEquipmentOptions?.isLoading ||
                  queryEquipmentOptions?.isFetching
                }
                onChange={(name, treeValue) => {
                  let safeValue = {
                    label: '',
                    value: '',
                  } as IOption;

                  if (treeValue) {
                    safeValue = {
                      label: treeValue.description,
                      value: treeValue.id,
                    };
                  }

                  handleAutoCompleteChange(name, safeValue);
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <Autocomplete
                id="executorId"
                className={classes.inputList}
                options={options?.executorOptions?.values}
                onChange={(e, selected) => {
                  return handleAutoCompleteChange(
                    'executorId',
                    selected as IOption,
                  );
                }}
                disabled
                value={defaultValueForExecutor}
                getOptionLabel={option => String(option.label)}
                getOptionSelected={getOptionSelected}
                noOptionsText={NO_OPTIONS_TEXT}
                renderInput={params => {
                  const extraProps = {
                    name: 'executorId',
                    label: 'Empresa',
                    onChange: handleFieldSearchTextChange,
                    error:
                      formik.touched.executorId &&
                      Boolean(formik.errors.executorId),
                    helperText:
                      (formik.touched.executorId && formik.errors.executorId) ||
                      DEFAULT_HELPER_TEXT,
                  };
                  return inputRenderer(
                    {
                      ...params,
                      ...extraProps,
                    },
                    options?.executorOptions?.isLoading,
                  );
                }}
              />
            </Grid>
          </Grid>
          {/* END ROW 2 */}

          {/* START ROW 4 */}
          <Grid container direction="row" alignItems="center" spacing={2}>
            <Grid item xs={4}>
              <TextField
                id="periodicity"
                name="periodicity"
                label="Periodicidade"
                variant="outlined"
                inputProps={{
                  type: 'number',
                }}
                onChange={event => {
                  const { value, name } = event.target;

                  if (value) {
                    formik.setFieldValue(name, Number(value));
                  } else {
                    formik.setFieldValue(name, null);
                  }
                }}
                onBlur={formik.handleBlur}
                value={formik.values.periodicity}
                error={
                  formik.touched.periodicity &&
                  Boolean(formik.errors.periodicity)
                }
                helperText={
                  (formik.touched.periodicity && formik.errors.periodicity) ||
                  OPTIONAL
                }
              />
            </Grid>

            <Grid item xs={4}>
              <Autocomplete
                id="inspectorTechnician"
                className={classes.inputList}
                options={inspectorTechnicianOptions}
                onChange={(e, selected) => {
                  return handleAutoCompleteChange(
                    'inspectorTechnician',
                    selected as IOption,
                  );
                }}
                defaultValue={{
                  value: shortName || 0,
                  label: shortName || '',
                }}
                disabled
                getOptionLabel={option => String(option.label)}
                getOptionSelected={getOptionSelected}
                noOptionsText={NO_OPTIONS_TEXT}
                renderInput={params => {
                  const extraProps = {
                    name: 'inspectorTechnician',
                    label: 'Executor',
                    error:
                      formik.touched.inspectorTechnician &&
                      Boolean(formik.errors.inspectorTechnician),
                    helperText:
                      (formik.touched.inspectorTechnician &&
                        formik.errors.inspectorTechnician) ||
                      DEFAULT_HELPER_TEXT,
                  };
                  return inputRenderer({ ...params, ...extraProps });
                }}
              />
            </Grid>

            <Grid item xs={4}>
              <TextField
                id="operatingCondition"
                name="operatingCondition"
                label="Condição de operação"
                variant="outlined"
                inputProps={{
                  maxLength: 200,
                }}
                onChange={formik.handleChange}
                value={formik.values.operatingCondition}
                error={
                  formik.touched.operatingCondition &&
                  Boolean(formik.errors.operatingCondition)
                }
                helperText={OPTIONAL}
              />
            </Grid>
          </Grid>
          {/* END ROW 4 */}

          {/* START ROW 5 */}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FormControl
                onChange={formik.handleChange}
                error={formik.touched.outage && Boolean(formik.errors.outage)}
              >
                <FormLabel className={classes.formLabel} component="legend">
                  <span className={classes.formInput}>
                    A atividade possui indisponibilidade de geração?
                  </span>
                  {options?.outageOptions?.isLoading && (
                    <span>
                      <CircularProgress color="inherit" size={25} />
                    </span>
                  )}
                </FormLabel>
                <RadioGroup
                  value={formik.values.outage}
                  id="outage"
                  name="outage"
                >
                  {options?.outageOptions?.values?.map(({ value, label }) => (
                    <FormControlLabel
                      value={value}
                      control={<Radio color="primary" />}
                      label={label}
                    />
                  ))}
                  <FormHelperText id="outage">
                    {(formik.touched.outage && formik.errors.outage) ||
                      DEFAULT_HELPER_TEXT}
                  </FormHelperText>
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
          {/* END ROW 5 */}

          {/* START ROW 6 */}
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FormControl
                onChange={formik.handleChange}
                error={
                  formik.touched.predictedUse &&
                  Boolean(formik.errors.predictedUse)
                }
              >
                <FormLabel className={classes.formLabel} component="legend">
                  <span className={classes.formInput}>
                    A atividade vai ser realizada em aproveitamento?
                  </span>
                  {options?.predictedUseOptions?.isLoading && (
                    <span>
                      <CircularProgress color="inherit" size={25} />
                    </span>
                  )}
                </FormLabel>
                <RadioGroup
                  value={formik.values.predictedUse}
                  id="predictedUse"
                  name="predictedUse"
                >
                  {options?.predictedUseOptions?.values?.map(
                    ({ value, label }) => (
                      <FormControlLabel
                        value={value}
                        control={<Radio color="primary" />}
                        label={label}
                      />
                    ),
                  )}
                  <FormHelperText id="predictedUse">
                    {(formik.touched.predictedUse &&
                      formik.errors.predictedUse) ||
                      DEFAULT_HELPER_TEXT}
                  </FormHelperText>
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>
          {/* END ROW 6 */}
        </Grid>
      </MuiPickersUtilsProvider>
    </form>
  );
};

export default FormAddInspection;
